import { type ReactElement } from 'react';
import { cn } from '@/lib/cn';
import { TEMPO_MULTIPLIERS } from './practice-controls-constants';
import { DroneControl } from '@/components/molecules/drone-control';
import { ResourceList } from '@/components/molecules/resource-list';
import type { DroneKey, DroneInterval, DroneOctave } from '@/lib/practice/drone-keys';
import type { ArrangementResource } from '@/lib/types';

export type PracticeControlsRailProps = {
  notes: string | null;
  resources?: ArrangementResource[];
  metronomeOn: boolean;
  onMetronomeOnChange: (value: boolean) => void;
  bpm: number;
  onBpmChange: (value: number) => void;
  activeMultiplier: number;
  onActiveMultiplierChange: (index: number) => void;
  droneRoot: DroneKey | null;
  onDroneRootChange: (root: DroneKey) => void;
  droneInterval: DroneInterval;
  onDroneIntervalChange: (interval: DroneInterval) => void;
  droneOctave: DroneOctave;
  onDroneOctaveChange: (octave: DroneOctave) => void;
  droneVolume: number;
  onDroneVolumeChange: (volume: number) => void;
  droneOn: boolean;
  onDroneOnChange: (value: boolean) => void;
  onDroneAutoMatch?: () => void;
  droneAutoMatchDisabled?: boolean;
};

export function PracticeControlsRail({
  notes,
  resources = [],
  metronomeOn,
  onMetronomeOnChange,
  bpm,
  onBpmChange,
  activeMultiplier,
  onActiveMultiplierChange,
  droneRoot,
  onDroneRootChange,
  droneInterval,
  onDroneIntervalChange,
  droneOctave,
  onDroneOctaveChange,
  droneVolume,
  onDroneVolumeChange,
  droneOn,
  onDroneOnChange,
  onDroneAutoMatch,
  droneAutoMatchDisabled,
}: PracticeControlsRailProps): ReactElement {

  return (
    <aside
      data-practice-controls-rail
      className="w-[280px] shrink-0 border-l border-[var(--rule)] flex flex-col bg-[var(--bg-surface)] text-[var(--ink)]"
    >
      <div className="h-16 px-5 flex items-center border-b border-[var(--rule)]">
        <div className="text-[13px] font-semibold">Controls</div>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase">Metronome</span>
            <button
              type="button"
              role="switch"
              aria-checked={metronomeOn}
              aria-label="Metronome"
              onClick={() => onMetronomeOnChange(!metronomeOn)}
              className={cn(
                'w-10 h-6 rounded-full p-0.5 transition-colors',
                metronomeOn ? 'bg-[var(--warm)]' : 'bg-[var(--bg-soft)]',
              )}
            >
              <span
                className={cn(
                  'block w-5 h-5 bg-[var(--bg-surface)] rounded-full transition-transform',
                  metronomeOn ? 'translate-x-4' : 'translate-x-0',
                )}
              />
            </button>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3">
            <button
              type="button"
              aria-label="Decrease BPM"
              onClick={() => onBpmChange(Math.max(0, bpm - 1))}
              className="w-8 h-8 rounded-[var(--r-sm)] text-[var(--ink-3)] hover:bg-[var(--bg-soft)]"
            >
              −
            </button>
            <div className="font-display text-[40px] font-semibold tabular-nums">{bpm}</div>
            <button
              type="button"
              aria-label="Increase BPM"
              onClick={() => onBpmChange(bpm + 1)}
              className="w-8 h-8 rounded-[var(--r-sm)] text-[var(--ink-3)] hover:bg-[var(--bg-soft)]"
            >
              +
            </button>
          </div>
          <div className="flex items-center gap-1 mt-2">
            {TEMPO_MULTIPLIERS.map((multiplier, i) => (
              <button
                key={multiplier}
                type="button"
                onClick={() => onActiveMultiplierChange(i)}
                className={cn(
                  'flex-1 h-7 text-[10px] font-mono rounded-[var(--r-sm)]',
                  i === activeMultiplier
                    ? 'bg-[var(--bg-soft)] text-[var(--ink)]'
                    : 'text-[var(--ink-3)] hover:bg-[var(--bg-soft)]',
                )}
              >
                {multiplier}
              </button>
            ))}
          </div>
        </div>

        <div className="h-px bg-[var(--rule)]" />

        <div className="-mx-5">
          <DroneControl
            surface="light"
            root={droneRoot}
            interval={droneInterval}
            octave={droneOctave}
            volume={droneVolume}
            on={droneOn}
            onRootChange={onDroneRootChange}
            onIntervalChange={onDroneIntervalChange}
            onOctaveChange={onDroneOctaveChange}
            onVolumeChange={onDroneVolumeChange}
            onOnChange={onDroneOnChange}
            onAutoMatch={onDroneAutoMatch}
            autoMatchDisabled={droneAutoMatchDisabled}
          />
        </div>

        {notes && (
          <>
            <div className="h-px bg-[var(--rule)]" />
            <div>
              <div className="text-[11px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase mb-2">Notes</div>
              <div className="p-3 rounded-[var(--r-sm)] bg-[var(--bg-soft)] border border-[var(--rule)] text-[12px] text-[var(--ink-3)] leading-relaxed">
                {notes}
              </div>
            </div>
          </>
        )}

        <div className="h-px bg-[var(--rule)]" />

        <section aria-labelledby="resources-heading">
          <h3
            id="resources-heading"
            className="text-[11px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase mb-2"
          >
            Resources
          </h3>
          <ResourceList resources={resources} />
        </section>

      </div>
    </aside>
  );
}
